<template>
  <div class="content-actions">
    <icon-button
      type="button"
      class="-ico-only -no-container"
      icon="more_vert"
      :label="isMenuOpen ? 'Action Menu Expanded' : 'Action Menu Collapsed'"
      :aria-expanded="isMenuOpen ? 'true' : 'false'"
      :aria-controls="menuId"
      data-tour="action-menu-cta"
      @click-action="handleMenuOpen"
    />
    <outside-click
      :ignore-query-selector="`#${modalId}`"
      :disabled="!isMenuOpen"
      @outside-click-action="handleMenuClose"
    >
      <div
        :id="menuId"
        class="content-actions-group"
        :class="{ '-open': isMenuOpen }"
        :aria-hidden="isMenuOpen ? 'false' : 'true'"
      >
        <div class="content-actions-close">
          <icon-button
            type="button"
            class="-ico-only -no-container"
            icon="close"
            label="Close Menu"
            @click-action="handleMenuClose"
          />
        </div>
        <ul class="content-actions-list">
          <!-- edit buttons -->
          <li v-if="editButton" class="content-action">
            <a
              v-if="entityType === 'custom_workout'"
              :href="`/my/custom-workouts/edit/${entityId}`"
              class="btn"
            >
              <i class="material-symbols-outlined" aria-hidden="true">edit</i>
              Edit Workout
            </a>
            <a
              v-if="entityType === 'plan' && entityCategory === 'routine'"
              :href="`/my/routines/${entityId}/edit-info`"
              class="btn"
            >
              <i class="material-symbols-outlined" aria-hidden="true">edit</i>
              Edit Routine
            </a>
          </li>

          <!-- favorite button -->
          <li
            v-if="favoriteButton && entityType !== 'plan' && entityType !== 'custom_workout'"
            class="content-action"
          >
            <FavoriteButton :entity-type="entityType" :entity-id="entityId" />
          </li>

          <!-- tags button -->
          <li v-if="tagsButton" class="content-action">
            <button class="btn" type="button" @click="openTagsModal">
              <i class="material-symbols-outlined" aria-hidden="true">label</i>
              Add Tags
            </button>
          </li>

          <!-- view plan detail, anchored to program guide -->
          <li v-if="viewProgramGuideButton && entityType === 'plan'" class="content-action">
            <a class="btn" :href="`/my/programs/${entityId}#guide`" @click="handleMenuClose">
              <i class="material-symbols-outlined" aria-hidden="true">article</i>
              Program Guide
            </a>
          </li>

          <!-- view meal plan shopping list -->
          <li v-if="viewShoppingListButton && entityType === 'plan'" class="content-action">
            <a class="btn" :href="`/my/programs/${entityId}/shopping-list`">
              <i class="material-symbols-outlined" aria-hidden="true">shopping_cart</i>
              Shopping List
            </a>
          </li>

          <!-- view scheduled instances for a plan -->
          <li v-if="viewScheduledButton" class="content-action">
            <a
              class="btn"
              :href="`/my/${
                entityCategory === 'routine' ? 'routines' : 'programs'
              }/${entityId}#scheduled-instances`"
              @click="handleMenuClose"
            >
              <i class="material-symbols-outlined" aria-hidden="true">schedule</i>
              View Scheduled
            </a>
          </li>

          <!-- view scheduled instances for a plan -->
          <li v-if="viewCommentsUrl" class="content-action">
            <a class="btn" :href="viewCommentsUrl" @click="handleMenuClose">
              <i class="material-symbols-outlined" aria-hidden="true">mode_comment</i>
              View Comments
            </a>
          </li>

          <!-- shelf button, only on workout plans -->
          <li
            v-if="shelfButton && features.SHELVES && entityType === 'plan'"
            class="content-action"
          >
            <ShelfButton :entity-type="entityType" :entity-id="entityId" />
          </li>

          <li v-if="planCopyButton && features.PLAN_COPY" class="content-action">
            <button class="btn" type="button" @click="openCopyModal">
              <i class="material-symbols-outlined" aria-hidden="true">content_copy</i>
              Copy to Routines
            </button>
          </li>

          <!-- find similar -->
          <li v-if="similarButton && features.SIMILAR_FILTERS" class="content-action">
            <button class="btn" type="button" @click="openSimilarModal">
              <i class="material-symbols-outlined -rotate-90" aria-hidden="true">pause_circle</i>
              Find Similar Content
            </button>
          </li>

          <!-- can add to routines -->
          <li v-if="addToRoutineButton && features.ROUTINES" class="content-action">
            <button class="btn" type="button" @click="openRoutineModal">
              <i class="material-symbols-outlined" aria-hidden="true">library_add</i>
              Add to a Routine
            </button>
          </li>

          <!-- export cta, works on recipes -->
          <li v-if="exportUrl" class="content-action">
            <export-url-button :url="exportUrl" />
          </li>

          <!-- custom buttons -->
          <slot />
        </ul>
      </div>
    </outside-click>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import type { PropType } from 'vue'
import { mapState, mapActions } from 'pinia'
import { useUserAuthStore } from '@/stores/user-auth'
import { useModalsStore } from '@/stores/modals'
import { useCalendarStore } from '@/stores/calendar'
import OutsideClick from '@/components/tool-tips/outside-click.vue'
import ExportUrlButton from '@/components/buttons/export-url-button.vue'
import FavoriteButton from '@/components/buttons/favorite-button.vue'
import ShelfButton from '@/components/buttons/shelf-button.vue'
import IconButton from '@/components/buttons/icon-button.vue'

export default defineComponent({
  name: 'ContentActionsButton',
  components: {
    IconButton,
    OutsideClick,
    ExportUrlButton,
    FavoriteButton,
    ShelfButton,
  },
  props: {
    entityId: {
      type: Number,
      required: true,
    },
    entityType: {
      type: String as PropType<
        'workout' | 'custom_workout' | 'plan' | 'article' | 'wellness_video'
      >,
      default: 'workout',
    },
    // differentiate between plan types: programs, challenges, routines
    entityCategory: {
      type: String,
      default: '',
      validator: (val: string) => ['', 'program', 'challenge', 'routine', 'recipe'].includes(val),
    },
    entityTitle: {
      type: String,
      default: '',
    },

    // explicit actions as the list is growing
    // all default to false
    favoriteButton: {
      type: Boolean,
      default: false,
    },
    similarButton: {
      type: Boolean,
      default: false,
    },
    tagsButton: {
      type: Boolean,
      default: false,
    },
    shelfButton: {
      type: Boolean,
      default: false,
    },
    addToRoutineButton: {
      type: Boolean,
      default: false,
    },
    editButton: {
      type: Boolean,
      default: false,
    },
    exportUrl: {
      type: String,
      default: '',
    },
    viewProgramGuideButton: {
      type: Boolean,
      default: false,
    },
    viewShoppingListButton: {
      type: Boolean,
      default: false,
    },
    viewScheduledButton: {
      type: Boolean,
      default: false,
    },
    planCopyButton: {
      type: Boolean,
      default: false,
    },
    viewCommentsUrl: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      isMenuOpen: false,
    }
  },

  computed: {
    ...mapState(useUserAuthStore, ['user', 'features']),

    menuId() {
      return `actions-${this.entityType}-${this.entityId}`
    },

    modalId() {
      return `menu-modals-${this.entityType}-${this.entityId}`
    },
  },

  methods: {
    ...mapActions(useCalendarStore, ['setRefreshScheduledInstances']),
    ...mapActions(useModalsStore, ['showLoginModal', 'openSimpleModal']),

    // main menu toggle
    handleMenuOpen() {
      this.isMenuOpen = true
    },

    handleMenuClose() {
      this.isMenuOpen = false
    },

    openSimilarModal() {
      // should never happen as user must be logged in for feature flag
      if (!this.user.isLoggedIn) {
        this.showLoginModal()
        return
      }

      this.handleMenuClose()

      this.openSimpleModal({
        type: 'SIMILAR',
        id: this.modalId,
        props: {
          entityId: this.entityId,
          entityType: this.entityType,
          entityTitle: this.entityTitle,
          entityCategory: this.entityCategory,
        },
      })
    },

    openTagsModal() {
      // should never happen as user must be logged in for feature flag
      if (!this.user.isLoggedIn) {
        this.showLoginModal()
        return
      }

      this.handleMenuClose()

      this.openSimpleModal({
        type: 'TAGS',
        id: this.modalId,
        props: {
          entityId: this.entityId,
          entityType: this.entityType,
          entityTitle: this.entityTitle,
          entityCategory: this.entityCategory,
        },
      })
    },

    openRoutineModal() {
      // should never happen as user must be logged in for feature flag
      if (!this.user.isLoggedIn) {
        this.showLoginModal()
        return
      }

      this.handleMenuClose()

      this.openSimpleModal({
        type: 'ROUTINE_ADD',
        id: this.modalId,
        props: {
          entityId: this.entityId,
          entityType: this.entityType,
          entityTitle: this.entityTitle,
          entityCategory: this.entityCategory,
        },
      })
    },

    openCopyModal() {
      // should never happen as user must be logged in for feature flag
      if (!this.user.isLoggedIn) {
        this.showLoginModal()
        return
      }

      // required for modal
      if (!this.entityId || !this.entityTitle) {
        return
      }

      this.handleMenuClose()

      this.openSimpleModal({
        type: 'PLAN_COPY',
        id: this.modalId,
        props: {
          copyType: 'plan',
          planShortName: this.entityTitle,
          planId: this.entityId,
          groupingId: null,
        },
      })
    },
  },
})
</script>
